import {
  UpdateWebclientConfigRequestDto,
  CreateWebclientConfigRequestDto,
} from '@conversed/shared/src/central/dto/webclient-config.dto';
import { useCallback } from 'react';
import {
  getWebclientConfigsEndpoint,
  createWebclientConfigEndpoint,
  updateWebclientConfigEndpoint,
  deleteWebclientConfigEndpoint,
  removeWebclientWhitelistedDomainEndpoint,
  addWebclientWhitelistEndpoint,
  createWebclientEndpoint,
} from '../../../api/webclientsApi';
import { Actions } from '../../../constants/actionTypes';
import { useMainDispatch } from '../../useMainDispatch';
import { toastErrorTranslated } from '../../../helpers/toast';

export const useWebclientActions = () => {
  const dispatch = useMainDispatch();

  const fetchWebclientConfigs = useCallback(async () => {
    try {
      const { data: webclient } = await getWebclientConfigsEndpoint();
      dispatch({
        type: Actions.SET_SETTINGS,
        payload: {
          webclient: {
            configs: webclient.configs,
            uuid: webclient.uuid,
            botId: webclient?.botId,
            environment: webclient?.environment,
            whitelistedDomains: webclient?.whitelistedDomains,
          },
        },
      });
    } catch (error: any) {
      if (error?.response?.data?.errorCode !== 'NOT_FOUND') {
        toastErrorTranslated(error);
      }
      dispatch({
        type: Actions.SET_SETTINGS,
        payload: {
          webclient: {
            configs: [],
            uuid: undefined,
            botId: undefined,
            environment: undefined,
            whitelistedDomains: undefined,
          },
        },
      });
    }
  }, [dispatch]);

  const createWebclient = useCallback(async () => {
    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      await createWebclientEndpoint();
      await fetchWebclientConfigs();
    } catch (error: any) {
      console.error(error);
      toastErrorTranslated(error);
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch, fetchWebclientConfigs]);

  const createWebclientConfig = useCallback(
    async (name: string, config: CreateWebclientConfigRequestDto['config']) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await createWebclientConfigEndpoint(name, config);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const updateWebclientConfig = useCallback(
    async (identifier: string, name: string, config: UpdateWebclientConfigRequestDto['config']) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await updateWebclientConfigEndpoint(identifier, name, config);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const deleteWebclientConfig = useCallback(
    async (identifier: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await deleteWebclientConfigEndpoint(identifier);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const addWhitelistedDomain = useCallback(
    async (domain: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await addWebclientWhitelistEndpoint(domain);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );
  const removeWebclientWhitelistedDomain = useCallback(
    async (whitelistedDomain: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await removeWebclientWhitelistedDomainEndpoint(whitelistedDomain);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  return {
    fetchWebclientConfigs,
    createWebclientConfig,
    createWebclient,
    updateWebclientConfig,
    deleteWebclientConfig,
    addWhitelistedDomain,
    removeWebclientWhitelistedDomain,
  };
};
